import { motion } from "framer-motion";
import { BookOpen, Lock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const FormationPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="formation-hero">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/00dc70e4-70e9-471a-a527-cf5ef1f5c6b5/images/f2cd200b3db4fc136cc5daabd7828ff2fb8403cc5bd88569b0aeef2fd67a6f97.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            {...fadeInUp}
          >
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-[0.2em] mb-4 block">
              Formation APM
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mb-6">
              Programme de<br />
              <span className="text-[#0066FF]">Formation</span>
            </h1>
            <p className="text-[#94A3B8] text-lg leading-relaxed">
              Une formation complète pour maîtriser toutes les compétences 
              nécessaires à votre réussite en tant que coach indépendant.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Formation Overview */}
      <section className="section-padding" data-testid="formation-overview">
        <div className="container-custom">
          <motion.div 
            className="max-w-4xl mx-auto"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            {/* Access Info */}
            <div className="bg-[#0F0F16] border border-white/10 p-8 md:p-12 mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-[#0066FF]/10 border border-[#0066FF]/30 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#0066FF]" />
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-2xl font-bold">Accès réservé</h2>
                  <p className="text-[#94A3B8]">aux coachs affiliés APM</p>
                </div>
              </div>
              
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                La formation APM est exclusivement accessible aux coachs affiliés. 
                Elle comprend des vidéos de formation, des documents téléchargeables 
                et un accompagnement personnalisé pour vous aider à atteindre 
                <span className="text-[#0066FF]"> 3000€/mois en 90 jours</span>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/coach/login"
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="formation-coach-login"
                >
                  <BookOpen className="w-5 h-5" />
                  Accéder à ma formation
                </Link>
                <Link
                  to="/apply"
                  className="px-6 py-4 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-all text-center"
                  data-testid="formation-apply"
                >
                  Devenir coach APM
                </Link>
              </div>
            </div>

            {/* What's included */}
            <h3 className="font-['Outfit'] text-xl font-bold uppercase mb-6 text-center">
              Ce que comprend la formation
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div 
                className="bg-[#0F0F16] border border-white/10 p-6 text-center"
                whileHover={{ borderColor: "rgba(0, 102, 255, 0.5)" }}
              >
                <div className="w-12 h-12 bg-[#0066FF]/10 flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-6 h-6 text-[#0066FF]" />
                </div>
                <h4 className="font-semibold mb-2">Vidéos de formation</h4>
                <p className="text-[#94A3B8] text-sm">
                  Des vidéos pratiques pour maîtriser les techniques de vente et fidélisation.
                </p>
              </motion.div>

              <motion.div 
                className="bg-[#0F0F16] border border-white/10 p-6 text-center"
                whileHover={{ borderColor: "rgba(0, 102, 255, 0.5)" }}
              >
                <div className="w-12 h-12 bg-[#0066FF]/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-[#0066FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="font-semibold mb-2">Documents</h4>
                <p className="text-[#94A3B8] text-sm">
                  Scripts, templates et ressources à télécharger pour votre activité.
                </p>
              </motion.div>

              <motion.div 
                className="bg-[#0F0F16] border border-white/10 p-6 text-center"
                whileHover={{ borderColor: "rgba(0, 102, 255, 0.5)" }}
              >
                <div className="w-12 h-12 bg-[#0066FF]/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-[#0066FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h4 className="font-semibold mb-2">Accompagnement</h4>
                <p className="text-[#94A3B8] text-sm">
                  Support personnalisé de Florian pour répondre à vos questions.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0F0F16]" data-testid="formation-cta">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-2xl mx-auto"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold uppercase tracking-tight mb-6">
              Prêt à transformer votre carrière ?
            </h2>
            <p className="text-[#94A3B8] mb-8">
              Rejoignez APM et accédez à notre formation complète pour devenir 
              un coach indépendant qui réussit.
            </p>
            <Link
              to="/apply"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="formation-cta-apply"
            >
              Postuler maintenant
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FormationPage;
